'use client';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@core/ui/primitives/tabs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge } from '@core/ui/primitives/badge';
import { Spinner } from '@core/ui/primitives/spinner';
import { OrganizationVaultCreateWizard } from '@vaults/ui/management/OrganizationVaultCreateWizard';
import type { OrganizationNavItem, OrganizationSidebarSelection } from './OrganizationSidebar';
import { CREATE_VAULT_LABEL } from './OrganizationSidebarNav';
import { AccessManagement as AccessManagementPanel } from './panels/AccessManagement';
import { RepositorySync as RepositorySyncPanel } from './panels/RepositorySync';
import { MetricsPanel as MetricsPanelPanel } from './panels/MetricsPanel';
import { OrganizationSettingsTabs } from './panels/OrganizationSettingsTabs';
import { VaultRecoveryPanel } from './panels/VaultRecoveryPanel';
import { LuTrash2, LuEllipsis, LuArrowRightLeft, LuSave, LuRefreshCw, LuSearchCode } from 'react-icons/lu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@core/ui/primitives/dropdown-menu';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@core/ui/primitives/alert-dialog';
import { Input } from '@core/ui/primitives/input';
import { Button } from '@core/ui/primitives/button';
import { Switch } from '@core/ui/primitives/switch';
import { Label } from '@core/ui/primitives/label';
import { useToast } from '@/core/ui/hooks/use-toast';
import { MoveVaultDialog } from '@vaults/ui/management/MoveVaultDialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/core/ui/primitives/tooltip';

const MOCK_VAULT_DETAILS = {
  'Design System': {
    name: 'Design System',
    description: 'Central hub for typography, components, and editorial guidelines.',
    sync: { status: 'Synced', provider: 'GitHub', lastSync: '3 minutes ago', attached: true },
    usage: { usedGb: 36, quotaGb: 100, docs: 148, drafts: 24, scheduled: 3 },
    credentials: { status: 'Healthy', detail: 'Personal access token expires in 27 days' },
  },
  'Product Roadmap': {
    name: 'Product Roadmap',
    description: 'Quarterly roadmap snapshots, OKRs, and release briefs.',
    sync: { status: 'Sync warning', provider: 'Azure DevOps', lastSync: '12 minutes ago', attached: true },
    usage: { usedGb: 62, quotaGb: 120, docs: 63, drafts: 9, scheduled: 1 },
    credentials: {
      status: 'Credential expiring soon',
      detail: 'Mirror PAT expires in 3 days. Publishing blocked until updated.',
    },
  },
  'Security Policies': {
    name: 'Security Policies',
    description: 'SOC2, ISO-27001 controls, and incident response playbooks.',
    sync: { status: 'Sync error', provider: 'Bitbucket', lastSync: '1 hour ago', attached: true },
    usage: { usedGb: 48, quotaGb: 90, docs: 41, drafts: 5, scheduled: 0 },
    credentials: {
      status: 'Webhook failing (403)',
      detail: 'Inbound webhook blocked by remote allowlist. Sync paused until resolved.',
    },
  },
  'Research Lab': {
    name: 'Research Lab',
    description: 'User research notes, transcripts, and experiment write-ups.',
    sync: { status: 'Not synced', provider: null, lastSync: '—', attached: false },
    usage: { usedGb: 28, quotaGb: 75, docs: 92, drafts: 31, scheduled: 6 },
    credentials: { status: 'Sync optional', detail: 'Repository sync disabled. Attach a remote to enable mirroring.' },
  },
} as const;

export function OrganizationContentPanel({
  selection,
  navItems,
  organizations,
  activeOrganization,
  userIsReadOnly,
  onSelect,
  onVaultCreated,
  onVaultDeleted,
  onOrganizationUpdated,
  onOrganizationDeleted,
  setHeaderActions,
}: {
  selection: OrganizationSidebarSelection;
  navItems: OrganizationNavItem[];
  organizations:
    | Array<{ id: string; name: string; slug?: string; vaults?: Array<{ id: string; role: string; privileges: string[] }> }>
    | undefined;
  activeOrganization?: { id: string; name: string; slug: string; role?: string | null; privileges?: string[] } | null;
  userIsReadOnly?: boolean;
  onSelect: (selection: OrganizationSidebarSelection) => void;
  onVaultCreated: (vault: { id: string; slug: string; title: string; organizationId: string }) => void | Promise<void>;
  onVaultDeleted?: () => void | Promise<void>;
  onOrganizationUpdated?: (organization: { id: string; name: string; slug: string }) => void;
  onOrganizationDeleted?: () => void;
  setHeaderActions?: (actions: React.ReactNode) => void;
}) {
  // Find vault section once and memoize cancel handler to keep stable reference.
  const vaultSection = navItems.find((item) => item.title === 'Vaults');
  const handleCancel = useCallback(() => {
    if (vaultSection?.items?.length) {
      // Prefer first non-deleted vault, fallback to first vault if all deleted
      const activeVaults = vaultSection.items.filter((v) => !v.isDeleted);
      const fallback = activeVaults[0] ?? vaultSection.items[0];
      onSelect({
        section: 'Vaults',
        label: fallback.title,
        vaultId: fallback.vaultId ?? null,
        vaultSlug: fallback.vaultSlug ?? null,
        organizationId: fallback.organizationId ?? null,
      });
    } else {
      onSelect({ section: 'Organization Settings', label: 'Organization Settings' });
    }
  }, [onSelect, vaultSection]);

  // Clear actions when switching away from Settings
  useEffect(() => {
    if (selection.section !== 'Organization Settings') {
      setHeaderActions?.(null);
    }
  }, [selection.section, setHeaderActions]);

  if (selection.section === 'Organization Settings') {
    if (!activeOrganization) {
      return (
        <div className='flex h-full items-center justify-center'>
          <p className='text-sm text-muted-foreground'>Select an organization to view settings.</p>
        </div>
      );
    }
    return (
      <OrganizationSettingsTabs
        organization={activeOrganization}
        onOrganizationUpdated={onOrganizationUpdated}
        onOrganizationDeleted={onOrganizationDeleted}
        setHeaderActions={setHeaderActions!}
      />
    );
  }

  if (selection.section === 'Vaults' && selection.label === CREATE_VAULT_LABEL) {
    const existingSlugs = (vaultSection?.items ?? [])
      .filter((item) => !selection.organizationId || item.organizationId === selection.organizationId)
      .map((item) => (item.vaultSlug ?? '').toLowerCase())
      .filter(Boolean);
    const activeOrg = organizations?.find((org) => org.id === (selection.organizationId ?? '')) ?? undefined;

    return (
      <div className='flex h-full flex-col'>
        <OrganizationVaultCreateWizard
          organizationId={selection.organizationId ?? null}
          organizationSlug={activeOrg?.slug ?? null}
          existingSlugs={existingSlugs}
          onCancel={handleCancel}
          onCreated={async (vault) => {
            if (!vault) {
              handleCancel();
              return;
            }
            await onVaultCreated(vault);
          }}
        />
      </div>
    );
  }

  // Find the current vault's privileges and role
  const { currentVaultPrivileges, currentVaultRole } = ((): { currentVaultPrivileges: string[]; currentVaultRole: string | null } => {
    if (!selection.vaultId || !organizations) return { currentVaultPrivileges: [], currentVaultRole: null };
    for (const org of organizations) {
      const vault = org.vaults?.find((v) => v.id === selection.vaultId);
      if (vault) return { currentVaultPrivileges: vault.privileges, currentVaultRole: vault.role };
    }
    return { currentVaultPrivileges: [], currentVaultRole: null };
  })();

  // Show recovery panel for deleted vaults
  if (selection.isDeleted && selection.vaultId) {
    // vault:create privilege allows recovery, vault:purge allows permanent deletion
    const canRecover = currentVaultPrivileges.includes('vault:create');
    const canPurge = currentVaultPrivileges.includes('vault:purge');
    return (
      <div className='flex h-full flex-col'>
        <VaultRecoveryPanel
          vaultId={selection.vaultId}
          vaultTitle={selection.label}
          onRecovered={onVaultDeleted}
          onPurged={onVaultDeleted}
          canRecover={canRecover}
          canPurge={canPurge}
        />
      </div>
    );
  }

  return (
    <div className='flex h-full flex-col'>
      <VaultManagementTabs selection={selection} vaultPrivileges={currentVaultPrivileges} vaultRole={currentVaultRole} userIsReadOnly={userIsReadOnly} onVaultDeleted={onVaultDeleted} onVaultMoved={onVaultDeleted} setHeaderActions={setHeaderActions} />
    </div>
  );
}

// Types for header action state from child panels
interface SaveState {
  isDirty: boolean;
  isSaving: boolean;
  canSave: boolean;
  onSave: () => void;
}

interface ReindexState {
  canReindex: boolean;
  isReindexing: boolean;
  reindexPercent: number;
  isReindexStuck: boolean;
  onReindex: () => void;
  onCancelReindex: () => void;
  // Refresh state
  canRefresh: boolean;
  isRefreshing: boolean;
  onRefresh: () => void;
}

function VaultManagementTabs({
  selection,
  vaultPrivileges,
  vaultRole,
  userIsReadOnly,
  onVaultDeleted,
  onVaultMoved,
  setHeaderActions,
}: {
  selection: OrganizationSidebarSelection;
  vaultPrivileges: string[];
  vaultRole: string | null;
  userIsReadOnly?: boolean;
  onVaultDeleted?: () => void | Promise<void>;
  onVaultMoved?: () => void | Promise<void>;
  setHeaderActions?: (actions: React.ReactNode) => void;
}) {
  const canDelete = vaultPrivileges.includes('vault:delete');
  const canPurge = vaultPrivileges.includes('vault:purge');
  const canMove = vaultPrivileges.includes('org:vault:move');

  // State from child panels for header actions
  const [saveState, setSaveState] = useState<SaveState | null>(null);
  const [reindexState, setReindexState] = useState<ReindexState | null>(null);
  const fallbackDetails =
    MOCK_VAULT_DETAILS[selection.label as keyof typeof MOCK_VAULT_DETAILS] ?? MOCK_VAULT_DETAILS['Design System'];

  const usagePercent = fallbackDetails
    ? Math.min(100, Math.round((fallbackDetails.usage.usedGb / fallbackDetails.usage.quotaGb) * 100))
    : 0;

  const [activeTab, setActiveTab] = useState<'access' | 'repository' | 'metrics'>('access');
  const [tabLoading, setTabLoading] = useState<boolean>(true);
  const loadingStartRef = useRef<number | null>(null);
  const { toast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteVaultName, setDeleteVaultName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPermanentDelete, setIsPermanentDelete] = useState(false);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [retentionDays, setRetentionDays] = useState<number>(90);
  const deleteInputRef = useRef<HTMLInputElement>(null);

  // Fetch retention days from config
  useEffect(() => {
    fetch('/api/config')
      .then((res) => res.json())
      .then((data) => {
        if (data.vault?.retentionDays) {
          setRetentionDays(data.vault.retentionDays);
        }
      })
      .catch(() => {
        // Keep default of 90 days on error
      });
  }, []);

  // Auto-focus delete input when dialog opens
  useEffect(() => {
    if (showDeleteDialog) {
      const timer = setTimeout(() => {
        deleteInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [showDeleteDialog]);

  // Handle Enter key in delete confirmation input
  const handleDeleteInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && deleteVaultName === selection.label && !isDeleting) {
      e.preventDefault();
      if (isPermanentDelete) {
        handlePermanentDelete();
      } else {
        handleDelete();
      }
    }
  };

  const handleDelete = async () => {
    if (!selection.vaultId) return;

    setIsDeleting(true);
    try {
      const response = await fetch(`/api/vaults/${selection.vaultId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vaultName: deleteVaultName }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete vault');
      }

      toast({
        title: 'Vault moved to trash',
        description: 'The vault has been soft-deleted and can be recovered by an administrator.',
        variant: 'success',
      });

      setShowDeleteDialog(false);
      // Await reload to ensure UI updates before releasing busy state
      await onVaultDeleted?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePermanentDelete = async () => {
    if (!selection.vaultId) return;

    setIsDeleting(true);

    try {
      // ONE call - purge handles soft-delete internally if needed
      const response = await fetch(`/api/vaults/${selection.vaultId}/purge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vaultName: deleteVaultName }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Failed to permanently delete vault (${response.status})`);
      }

      toast({
        title: 'Vault permanently deleted',
        description: 'The vault and all its data have been permanently removed.',
        variant: 'success',
      });

      setShowDeleteDialog(false);
      // Await reload to ensure UI updates before releasing busy state
      await onVaultDeleted?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const startLoading = () => {
    loadingStartRef.current = Date.now();
    setTabLoading(true);
  };

  const finishLoading = (opts?: { immediate?: boolean }) => {
    const started = loadingStartRef.current ?? Date.now();
    const elapsed = Date.now() - started;
    const min = opts?.immediate ? 0 : 200; // skip delay if content was sourced from cache
    const delay = Math.max(0, min - elapsed);
    window.setTimeout(() => {
      // Defer reveal to the next frame to let layout settle
      requestAnimationFrame(() => {
        setTabLoading(false);
        loadingStartRef.current = null;
      });
    }, delay);
  };

  // When vault selection changes, reset to access tab and show spinner until the new panel signals ready
  useEffect(() => {
    setActiveTab('access');
    startLoading();
    setDeleteVaultName(''); // Reset delete input on change
    setIsPermanentDelete(false);
  }, [selection.vaultId]);

  // Reset input when dialog is closed/cancelled
  useEffect(() => {
    if (!showDeleteDialog) {
      setDeleteVaultName('');
      setIsPermanentDelete(false);
    }
  }, [showDeleteDialog]);

  // Render save button in header actions (only on access tab when dirty)
  useEffect(() => {
    const showSave = activeTab === 'access' && saveState?.isDirty;

    if (!showSave) {
      setHeaderActions?.(null);
      return;
    }

    const actions = (
      <div className='flex items-center gap-1'>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type='button'
              className='inline-flex items-center justify-center h-7 w-7 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50'
              aria-label='Save changes'
              disabled={!saveState.canSave || saveState.isSaving}
              onClick={saveState.onSave}>
              {saveState.isSaving ? <Spinner className='h-3.5 w-3.5' /> : <LuSave className='h-3.5 w-3.5' />}
            </button>
          </TooltipTrigger>
          <TooltipContent side='bottom'>Save changes</TooltipContent>
        </Tooltip>
      </div>
    );
    setHeaderActions?.(actions);
  }, [activeTab, saveState, setHeaderActions]);

  return (
    <div className='flex h-full min-h-0 flex-col gap-6'>
      <Tabs
        value={activeTab}
        onValueChange={(v) => {
          // Gate the transition with a spinner until the target panel is ready
          setActiveTab(v as any);
          startLoading();
        }}
        className='flex h-full min-h-0 flex-col'>
        <div className='flex shrink-0 flex-wrap items-center justify-between gap-3'>
          <TabsList>
            <TabsTrigger value='access' className='border-0'>Access</TabsTrigger>
            <TabsTrigger value='repository' className='border-0'>Repository import</TabsTrigger>
            <TabsTrigger value='metrics' className='border-0'>Metrics</TabsTrigger>
          </TabsList>

          <div className='flex items-center gap-1'>
            {/* Refresh button - visible on all tabs when user has permission */}
            {reindexState?.canRefresh && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type='button'
                    className='inline-flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50'
                    aria-label='Refresh repository'
                    disabled={reindexState.isRefreshing}
                    onClick={reindexState.onRefresh}>
                    {reindexState.isRefreshing ? (
                      <Spinner className='h-4 w-4' />
                    ) : (
                      <LuRefreshCw className='h-4 w-4' />
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side='bottom'>
                  {reindexState.isRefreshing ? 'Refreshing…' : 'Refresh repository'}
                </TooltipContent>
              </Tooltip>
            )}

            {/* Reindex button - visible on all tabs when user has permission */}
            {reindexState?.canReindex && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type='button'
                    className={`inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50 ${
                      reindexState.isReindexStuck
                        ? 'text-amber-500 hover:text-amber-600'
                        : 'text-muted-foreground'
                    }`}
                    aria-label={reindexState.isReindexStuck ? 'Dismiss progress (jobs continue)' : 'Rebuild search index'}
                    disabled={reindexState.isReindexing && !reindexState.isReindexStuck}
                    onClick={reindexState.isReindexStuck ? reindexState.onCancelReindex : reindexState.onReindex}>
                    {reindexState.isReindexing ? (
                      <Spinner className='h-4 w-4' />
                    ) : (
                      <LuSearchCode className='h-4 w-4' />
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side='bottom'>
                  {reindexState.isReindexStuck
                    ? 'Indexing taking too long - click to dismiss (jobs continue on server)'
                    : reindexState.isReindexing
                      ? reindexState.reindexPercent > 0
                        ? `Indexing ${reindexState.reindexPercent}%`
                        : 'Indexing…'
                      : 'Rebuild search index'}
                </TooltipContent>
              </Tooltip>
            )}

            {/* More actions dropdown */}
            {(canMove || canDelete) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant='ghost' size='icon' className='h-8 w-8'>
                    <LuEllipsis className='h-4 w-4' />
                    <span className='sr-only'>More actions</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  {canMove && (
                    <DropdownMenuItem onSelect={() => setShowMoveDialog(true)}>
                      <LuArrowRightLeft className='mr-2 h-4 w-4' />
                      Move Vault
                    </DropdownMenuItem>
                  )}
                  {canDelete && (
                    <DropdownMenuItem
                      className='text-destructive focus:text-destructive'
                      onSelect={() => setShowDeleteDialog(true)}>
                      <LuTrash2 className='mr-2 h-4 w-4' />
                      Delete Vault
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Vault</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className='space-y-3'>
                    {!isPermanentDelete ? (
                      <>
                        <p>
                          This will <strong>move the vault to trash</strong>. The vault will be hidden from navigation
                          and inaccessible to users, but can be recovered by an administrator.
                        </p>
                        <p className='text-sm mt-2'>
                          <strong>Retention period:</strong> You have <strong>{retentionDays} days</strong> to recover this vault. After that, it will be permanently deleted.
                        </p>
                        <p className='text-xs text-muted-foreground mt-1'>
                          Settings, drafts, and access permissions will be preserved during recovery.
                        </p>
                      </>
                    ) : (
                      <>
                        <p className='text-destructive font-medium'>
                          This will <strong>permanently delete</strong> the vault and all its data. This action is
                          irreversible.
                        </p>
                        <p className='text-xs'>
                          All files, drafts, comments, access permissions, and settings will be permanently removed.
                          The content folder on disk will also be deleted.
                        </p>
                      </>
                    )}
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>

              <div className='py-4 space-y-4'>
                {canPurge && (
                  <div className='flex items-center space-x-3 p-3 rounded-md border border-destructive/30 bg-destructive/5'>
                    <Switch
                      id='permanent-delete'
                      checked={isPermanentDelete}
                      onCheckedChange={setIsPermanentDelete}
                    />
                    <Label
                      htmlFor='permanent-delete'
                      className='text-sm font-medium text-destructive cursor-pointer select-none'>
                      Delete permanently (cannot be recovered)
                    </Label>
                  </div>
                )}

                <div className='space-y-2'>
                  <p className='text-sm text-muted-foreground'>
                    Please type <span className='font-bold select-none'>{selection.label}</span> to confirm.
                  </p>
                  <Input
                    ref={deleteInputRef}
                    value={deleteVaultName}
                    onChange={(e) => setDeleteVaultName(e.target.value)}
                    onKeyDown={handleDeleteInputKeyDown}
                    placeholder='Enter vault name'
                  />
                </div>
              </div>

              <AlertDialogFooter>
                <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                <Button
                  variant='destructive'
                  disabled={deleteVaultName !== selection.label || isDeleting}
                  onClick={isPermanentDelete ? handlePermanentDelete : handleDelete}>
                  {isDeleting ? <Spinner className='mr-2 h-4 w-4' /> : null}
                  {isPermanentDelete ? 'Delete Permanently' : 'Move to Trash'}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {selection.vaultId && selection.vaultSlug && selection.organizationId && (
            <MoveVaultDialog
              open={showMoveDialog}
              onOpenChange={setShowMoveDialog}
              vaultId={selection.vaultId}
              vaultSlug={selection.vaultSlug}
              vaultTitle={selection.label}
              currentOrganizationId={selection.organizationId}
              onMoved={onVaultMoved}
            />
          )}
        </div>

        <TabsContent value='access' className='flex-1 min-h-0 overflow-hidden'>
          <div className='relative h-full'>
            {tabLoading && (
              <div className='absolute inset-0 z-10 flex items-center justify-center'>
                <Badge variant='outline' className='gap-2'>
                  <Spinner />
                  Loading
                </Badge>
              </div>
            )}
            <div
              className={`h-full transition-opacity duration-300 ease-out ${tabLoading ? 'opacity-0' : 'opacity-100'}`}>
              <AccessManagementPanel selection={selection} fallbackDetails={fallbackDetails} onReady={finishLoading} onSaveStateChange={setSaveState} />
            </div>
          </div>
        </TabsContent>
        {/* forceMount ensures RepositorySyncPanel initializes reindex/refresh state immediately,
            so buttons appear on all tabs without having to visit this tab first */}
        <TabsContent value='repository' className='flex-1 min-h-0 data-[state=inactive]:hidden' forceMount>
          <div className='relative h-full min-h-0'>
            {tabLoading && activeTab === 'repository' && (
              <div className='absolute inset-0 z-10 flex items-center justify-center'>
                <Badge variant='outline' className='gap-2'>
                  <Spinner />
                  Loading
                </Badge>
              </div>
            )}
            <div
              className={`h-full min-h-0 transition-opacity duration-300 ease-out ${
                tabLoading && activeTab === 'repository' ? 'opacity-0' : 'opacity-100'
              }`}>
              <RepositorySyncPanel details={fallbackDetails} vaultId={selection.vaultId} vaultPrivileges={vaultPrivileges} vaultRole={vaultRole} userIsReadOnly={userIsReadOnly} onReady={finishLoading} onReindexStateChange={setReindexState} />
            </div>
          </div>
        </TabsContent>
        <TabsContent value='metrics' className='flex-1 min-h-0 overflow-hidden'>
          <div className='relative h-full'>
            {tabLoading && (
              <div className='absolute inset-0 z-10 flex items-center justify-center'>
                <Badge variant='outline' className='gap-2'>
                  <Spinner />
                  Loading
                </Badge>
              </div>
            )}
            <div
              className={`h-full transition-opacity duration-300 ease-out ${tabLoading ? 'opacity-0' : 'opacity-100'}`}>
              <MetricsPanelPanel details={fallbackDetails} usagePercent={usagePercent} onReady={finishLoading} />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
